/* eslint-disable no-unused-expressions */
import { createStyles, Grid, makeStyles } from '@material-ui/core';
import moment from 'moment';
import { MUIDataTableColumn, MUIDataTableMeta } from 'mui-datatables';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import CustomDataTable from '../../../../../components/CustomDataTable';
import CustomDialog, {
  CustomDialogProps,
} from '../../../../../components/CustomDialog';
import SelectPeriod from '../../../../../components/SelectPeriod';
import TableSkeleton from '../../../../../components/TableSkeleton';
import { PeriodDTO } from '../../../../../models/dto/PeriodDTO';
import { PeriodOccurrencesEnum } from '../../../../../models/enum/PeriodOccurrencesEnum';
import { AppState } from '../../../../../store';
import { getPeriodReferencePunchCard } from '../../../../../store/actions/punchCardActions';
import { CondominiumReducerData } from '../../../../../store/reducers/condominiumReducer';
import { PunchCardReducerData } from '../../../../../store/reducers/punchCardReducer';

export interface PeriodDetailedStatusTableData {
  employeeName: string;
  registerDate: string;
  registers: string[];
  occurrences: string[];
}

interface OccurrencesPeriodDetailedStatusDialogProps extends CustomDialogProps {
  occurrencesTypeSelected: string[];
  showDialogDataByPeriod?: boolean;
  period: PeriodDTO | null;
  handleClose: () => void;
}

const useStyles = makeStyles(() =>
  createStyles({
    dialogContainer: {
      overflowY: 'hidden',
    },
  }),
);

const OccurrencesPeriodDetailedStatusDialog: React.FC<OccurrencesPeriodDetailedStatusDialogProps> = ({
  occurrencesTypeSelected,
  title,
  open,
  handleClose,
}) => {
  const dispatch = useDispatch();
  const classes = useStyles();
  const [periodSelected, setPeriodSelected] = useState<PeriodDTO | null>(null);
  const [
    periodDetailedStatusTableData,
    setPeriodDetailedStatusTableData,
  ] = useState<PeriodDetailedStatusTableData[]>([]);

  const {
    loadingPeriodReferencePunchCard,
    periodReferencePunchCard,
  }: PunchCardReducerData = useSelector(
    (state: AppState) => state.punchCardReducer,
  );

  const { condominiumSelected }: CondominiumReducerData = useSelector(
    (state: AppState) => state.condominiumReducer,
  );

  const handleChangePeriodSelected = (period: PeriodDTO) => {
    if (condominiumSelected) {
      setPeriodSelected(period);
      dispatch(
        getPeriodReferencePunchCard(
          moment(period.inicioMesApuracao, 'DD/MM/YYYY').format('YYYY-MM-DD'),
          moment(period.fimMesApuracao, 'DD/MM/YYYY').format('YYYY-MM-DD'),
          condominiumSelected.referencia,
        ),
      );
    }
  };

  const columns: MUIDataTableColumn[] = [
    {
      name: 'employeeName',
      label: 'Colaborador',
    },
    {
      name: 'registerDate',
      label: 'Data',
      options: {
        customBodyRender: (
          value: string,
          _tableMeta: MUIDataTableMeta,
          _updateValue: any,
        ) => moment(value, 'YYYY-MM-DD').format('DD/MM/YYYY'),
      },
    },
    {
      name: 'registers',
      label: 'Marcações',
      options: {
        filter: false,
        sort: false,
        customBodyRender: (
          value: string[],
          _tableMeta: MUIDataTableMeta,
          _updateValue: any,
        ) => value?.join(' - '),
      },
    },
    {
      name: 'occurrences',
      label: 'Ocorrências',
      options: {
        sort: false,
        customBodyRender: (
          value: string[],
          _tableMeta: MUIDataTableMeta,
          _updateValue: any,
        ) => value?.join(' - '),
      },
    },
  ];

  useEffect(() => {
    const tableData: PeriodDetailedStatusTableData[] = [];

    if (occurrencesTypeSelected.length === 0) {
      periodReferencePunchCard?.forEach(punchCard => {
        punchCard.pontos?.forEach(registers => {
          if (
            registers.ocorrencias?.some(
              register =>
                register ===
                  PeriodOccurrencesEnum.FALTA_SEM_JUSTIFICATIVA.description ||
                register ===
                  PeriodOccurrencesEnum.ATRASOS_SEM_JUSTIFICATIVA.description ||
                register ===
                  PeriodOccurrencesEnum.SAIDA_ANTECIPADA_SEM_JUSTIFICATIVA
                    .description,
            ) &&
            !registers.controleOcorrencia
          ) {
            tableData.push({
              employeeName: punchCard.funcionario.nome,
              occurrences: registers.ocorrencias,
              registerDate: registers.data,
              registers: registers.horarios,
            });
          }
        });
      });
    } else {
      periodReferencePunchCard?.forEach(punchCard => {
        punchCard.pontos?.forEach(registers => {
          let alreadyIncluded = false;
          registers.ocorrencias?.forEach(ocorrencia => {
            if (
              occurrencesTypeSelected.includes(ocorrencia) &&
              !alreadyIncluded
            ) {
              tableData.push({
                employeeName: punchCard.funcionario.nome,
                occurrences: registers.ocorrencias,
                registerDate: registers.data,
                registers: registers.horarios,
              });
              alreadyIncluded = true;
            }
          });
        });
      });
    }

    setPeriodDetailedStatusTableData(tableData);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [periodReferencePunchCard, occurrencesTypeSelected]);

  return (
    <CustomDialog
      maxWidth="lg"
      containerStyle={classes.dialogContainer}
      handleClose={handleClose}
      showCloseButton
      showOneButton
      title={title}
      contentDividers
      open={open}
    >
      <Grid container direction="column" justify="space-between">
        <Grid item xs={12}>
          <Grid container justify="space-between">
            <Grid item>
              <SelectPeriod
                showLabel
                disableUnderline
                value={periodSelected}
                handlePeriodChange={handleChangePeriodSelected}
              />
            </Grid>
          </Grid>
        </Grid>
        <Grid item xs={12}>
          {loadingPeriodReferencePunchCard ? (
            <TableSkeleton rows={4} />
          ) : (
            <CustomDataTable
              data={periodDetailedStatusTableData.sort((a, b) =>
                a.employeeName > b.employeeName ? 1 : -1,
              )}
              columns={columns}
              options={{
                filter: true,
                elevation: 0,
              }}
              title=""
            />
          )}
        </Grid>
      </Grid>
    </CustomDialog>
  );
};

export default OccurrencesPeriodDetailedStatusDialog;
